import { TextInput } from '@/UIComponents2/TextInput'
import { UnlockWithPro } from '@/components/UnlockWithPro'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { cn } from '@/utlis/cn'
import { ApiRoomRequest } from '@/RoomContext'
import { PasswordInput } from '@/UIComponents2/PasswordInput'
import { useUserContext } from '@/UserContext'
import { useTranslation } from 'react-i18next'
import { useIsProUser } from '@/services/useIsProUser'
import ElectronIPC from '@/services/ElectronIPC'
import { Button } from '@/UIComponents2/Button'
import { env } from '@/services/EnvironmentVariables'

type SettingsTabProps = {
    value: ApiRoomRequest
    onChange: (v: ApiRoomRequest) => void
    error?: { field?: string; message?: string; code?: string }
}
export function SettingsTab({ value, onChange, error }: SettingsTabProps) {
    const { t } = useTranslation()
    const [userState] = useUserContext()

    const maxMinutes = userState.profile?.policy?.roomTierPolicy.sessionDurationLimitMins
    const maxDisplayMinutes = formatMinutes(maxMinutes)
    const allowCustomSessionCode = userState.profile?.policy?.allowCustomSessionCode ?? false

    return (
        <div className="w-full flex flex-col gap-4">
            <Section title={t('Name')} centerTitle>
                <TextInput onChange={(v) => onChange({ ...value, name: v })} placeholder="David's Room" />
            </Section>
            {allowCustomSessionCode && (
                <Section title={t('Custom Session Code')} centerTitle>
                    <TextInput
                        placeholder="muse-camp-2025"
                        onChange={(v) => onChange({ ...value, customJoinCode: v })}
                        error={
                            error?.field === 'customJoinCode'
                                ? (error?.message ?? 'Invalid custom session code')
                                : undefined
                        }
                    />
                </Section>
            )}
            <Section title={t('Password')} centerTitle>
                <PasswordInput
                    onChange={(v) => onChange({ ...value, password: v })}
                    placeholder={'(' + t('optional') + ')'}
                />
            </Section>
            <Section title={t('Duration')}>
                <div className="flex items-center justify-between">
                    <div>
                        <span>00:00</span>
                        <span className="font-light opacity-75"> / {maxDisplayMinutes}</span>
                    </div>
                    <UnlockWithPro reason="sessionDurationLimitRunningOut" />
                </div>
            </Section>
            <Section title={t('Participants')}>
                <div className="flex items-center justify-between">
                    <div>
                        <span>1</span>
                        <span className="font-light opacity-75">
                            {' '}
                            / {userState.profile?.policy?.roomTierPolicy.maxUserCount}
                        </span>
                    </div>
                    <UnlockWithPro reason="premiumFeature:noCollaboratorCountLimit" />
                    <UnlockMoreParticipantsButton />
                </div>
            </Section>
            <Section title={t('Options')} className="border-b-0">
                <div className="flex flex-col gap-4">
                    <Checkbox
                        label={t('Participants join muted')}
                        onChange={(v) => onChange({ ...value, allParticipantsJoinMuted: v })}
                    />
                    <Checkbox
                        label={t('Enable waiting room')}
                        checked={!value.allowUserAutoJoin}
                        onChange={(v) => onChange({ ...value, allowUserAutoJoin: !v })}
                    />
                    {value.permanent && (
                        <Checkbox
                            label={t('Clear chat after session')}
                            checked={value.resetAfterSession}
                            onChange={(v) => onChange({ ...value, resetAfterSession: v })}
                        />
                    )}
                </div>
            </Section>
        </div>
    )
}

type SectionProps = {
    title: string
    centerTitle?: boolean
    children: React.ReactNode
    className?: string
}
function Section({ children, title, className, centerTitle }: SectionProps) {
    return (
        <div
            className={cn(
                'w-full grid grid-cols-2 border-b-2 border-[rgba(255,255,255,0.02)] px-8 pb-4 last:pb-0',
                className
            )}
        >
            <div className={cn('t-base-medium', centerTitle && 'self-center')}>{title}</div>
            <div>{children}</div>
        </div>
    )
}

function formatMinutes(minutes: number | undefined): string {
    if (!minutes) {
        return '∞'
    }
    return `${minutes}:00`
}

function UnlockMoreParticipantsButton() {
    const { t } = useTranslation()
    const isProUser = useIsProUser()

    const handleClick = () => {
        ElectronIPC.openLinkInBrowser('https://musesessions.co/contact')
    }

    if (!isProUser) {
        return null
    }

    if (!env.IS_ELECTRON) {
        return (
            <a href="https://musesessions.co/contact" target="_blank" className="text-muse-yellow hover:underline">
                {t('Unlock more')}
            </a>
        )
    }

    return (
        <Button variant="link" onClick={handleClick}>
            {t('Unlock more')}
        </Button>
    )
}
